import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Button, Row, Col } from 'reactstrap';
import { connect } from 'react-redux';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  Intl, LocalTable, ModalInfo,
} from 'components';
import bg from 'assets/img/step3Bg.png';
import SettingsActions from 'store/reducers/Settings';
import CampaignService from 'api/Campaign';
import { handlePromise } from 'utils';
import GenericContactModal from '../GenericContactModal';

const ContactCrmModal = ({
  campaignId, values, open, onAddContacts, onClose, toggleErrorAlert, toggleLoading,
}) => {
  const [contacts, setContacts] = useState(_.filter(values.contacts || [], { type: 'crm' }));
  const [tagPage, setTagPage] = useState(1);
  const [invalidContactsModalOpened, setInvalidContactsModalOpened] = useState(false);
  const [localError, setLocalErrors] = useState(null);
  const invalidContactsRef = useRef([]);
  const [data, setData] = useState([]);
  const [dataLoading, setDataLoading] = useState();
  const [loadMore, setLoadMore] = useState(false);

  useEffect(() => {
    const getData = async () => {
      setDataLoading(true);
      const [errors, response, responseData] = await handlePromise(
        CampaignService.getCrmData(campaignId, {
          type: 'tag',
          name: 'teamleader',
          page: tagPage,
        }),
      );
      setDataLoading(false);

      if (!response.ok) {
        return toggleErrorAlert(errors);
      }
      const newContacts = _.map(responseData.data, (list) => (
        { ...list, selected: _.findIndex(contacts, { owner: list.tag }) !== -1 }));
      setLoadMore(newContacts.length >= 50);
      setData([...data, ...newContacts]);
    };
    if (open) getData();
    else if (!_.isEqual(data, [])) setData([]);
  }, [open, tagPage]); //eslint-disable-line

  const toggleSelected = useCallback((row) => {
    if (row.original.id) {
      const newContacts = _.cloneDeep(contacts);
      const contactIndex = _.findIndex(newContacts, { id: row.original.id });
      if (contactIndex !== -1) {
        newContacts[contactIndex].selected = !newContacts[contactIndex].selected;
      }
      setContacts(newContacts);
    }
  }, [contacts]);

  const toggleInvalidContactsModal = useCallback((invalidContacts = []) => {
    invalidContactsRef.current = invalidContacts;
    setInvalidContactsModalOpened(!invalidContactsModalOpened);
  }, [invalidContactsModalOpened]);

  const toggleSelectedList = useCallback(async (row) => {
    if (row.original.tag) {
      const newLists = _.cloneDeep(data);
      const listIndex = _.findIndex(newLists, { tag: row.original.tag });
      let newContacts = _.cloneDeep(contacts);

      if (listIndex !== -1) {
        const item = newLists[listIndex];
        if (!item.selected) {
          toggleLoading(true);
          const [, responseContact, responseDataContact] = await handlePromise(
            CampaignService.getCrmData(campaignId, { type: 'contacts', name: 'teamleader', tag: row.original.tag }),
          );
          if (!responseContact.ok) {
            toggleLoading(false);
            return setLocalErrors('apiError');
          }
          if (localError) setLocalErrors(null);

          const tagContacts = _.map(responseDataContact.data, (contact) => {
            const phoneNumber = _.get(contact, 'telephones[0].number');
            return {
              nameId: `${contact.first_name || ''} ${contact.last_name || ''}`,
              phoneNumber: phoneNumber ? phoneNumber.replace(/ |\+/g, '') : null,
              phoneNumberCountryCode: values.phoneNumberCountryCode,
            };
          });

          const [, response, responseData] = await handlePromise(
            CampaignService.validatePhone(campaignId, { phoneNumbers: tagContacts }),
          );
          toggleLoading(false);
          if (!response.ok) return setLocalErrors('apiError');

          const invalidPhones = _.filter(responseData, { valid: false });
          const validPhones = _.filter(responseData, { valid: true });
          newContacts = newContacts.concat(_.map(validPhones, (phone) => ({
            ...phone,
            owner: item.tag,
            id: uuidv4(),
            selected: true,
            type: 'crm',
          })));
          if (invalidPhones.length) {
            console.log(invalidPhones, 'invalidPhones');
            toggleInvalidContactsModal(invalidPhones);
          }
        } else {
          newContacts = _.filter(newContacts, (contact) => contact.owner !== item.tag);
        }
        newLists[listIndex].selected = !item.selected;
        setData(newLists);
        setContacts(newContacts);
        console.log(newContacts,'newContacts');
      }
    }
  }, [contacts, data, campaignId, values.phoneNumberCountryCode, localError,
    setData, toggleInvalidContactsModal, toggleLoading]);

  return (
    <ModalInfo
      title="contactsCrmModalTitle"
      size="lg"
      open={open}
      isLoading={dataLoading}
      onClose={onClose}
      footer={(
        <Button
          color="app-default"
          size="app-sm"
          className="font-weight-700"
          type="button"
          onClick={() => onAddContacts(contacts, 'crm')}
        >
          <Intl id="save" />
        </Button>
      )}
    >
      <>
        {!!localError
          && <div className="error text-md text-center"><Intl id={localError} /></div>}

        <Row className="my-3">
          <Col md={7}>
            <div className="d-flex flex-column justify-content-center pl-md-0" style={{ minHeight: 260 }}>
              <img
                src={bg}
                alt="..."
                className="campaign-wizard-triangle img-fluid d-none d-lg-block"
                style={{ minHeight: 260 }}
              />

              <div className="w-100 d-flex">
                <Col md={10} xs={12} className="m-auto campaign-wizard-table campaign-wizard-table-w-100 pl-0 pr-0 pr-md-3">
                  <LocalTable
                    data={data.length < 5
                      ? data.concat(new Array(5 - data.length).fill({ empty: true }))
                      : data}
                    manualPagination
                    onlyTable
                    getRowProps={(row) => ({
                      onClick: () => toggleSelectedList(row),
                      className: row.original.selected ? 'bg-orange-6' : '',
                    })}
                    columns={[
                      {
                        Header: <Intl
                          id="selectedLists"
                          values={{
                            selected: _.filter(data, { selected: true }).length,
                            total: data.length,
                          }}
                        />,
                        disableTranslate: true,
                        accessor: 'tag',
                        style: { textAlign: 'left' },
                      },
                    ]}
                  />
                </Col>
              </div>
            </div>
            {!!loadMore
              && (
                <div className="text-center">
                  <Button
                    color="app-default"
                    size="app-sm"
                    onClick={() => setTagPage((state) => state + 1)}
                  >
                    <Intl id="loadMore" />
                  </Button>
                </div>
              )}
          </Col>

          <Col md={5} className="mt-3 mt-md-0 d-flex pl-md-0 align-items-md-center campaign-wizard-table campaign-wizard-table-w-100">
            <LocalTable
              data={contacts.length < 5
                ? contacts.concat(new Array(5 - contacts.length).fill({ empty: true }))
                : contacts}
              manualPagination
              onlyTable
              getRowProps={(row) => ({
                onClick: () => toggleSelected(row),
                className: row.original.selected ? 'bg-orange-6' : '',
              })}
              columns={[
                {
                  Header: 'nameOrId',
                  accessor: 'nameId',
                  customWidth: 110,
                },
                {
                  Header: <Intl
                    id="phonesCount"
                    values={{
                      selected: _.filter(contacts, { selected: true }).length,
                      total: contacts.length,
                    }}
                  />,
                  disableTranslate: true,
                  accessor: 'phoneNumber',
                  customWidth: 185,
                },
              ]}
            />
          </Col>
        </Row>

        {invalidContactsModalOpened
          && (
            <GenericContactModal
              contacts={invalidContactsRef.current}
              modalTitle="wrongPhones"
              tableTitle="wrongPhones"
              open={invalidContactsModalOpened}
              onClose={toggleInvalidContactsModal}
              type="error"
            />
          )}
      </>
    </ModalInfo>
  );
};

const mapDispatchToProps = ({
  toggleLoading: SettingsActions.toggleLoading,
  toggleErrorAlert: SettingsActions.toggleErrorAlert,
});

export default connect(null, mapDispatchToProps)(ContactCrmModal);
